import React from "react";
import moment from 'moment';
import { jsPDF } from "jspdf";
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { Backdrop, CircularProgress } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import withStyles from '@mui/styles/withStyles';
import { arraysMatch } from './helper';
import { poppinsRegularBase64, poppinsBoldBase64, correctIcon, inCorrectIcon, unansweredIcon, timeIcon } from "./quizReportUtils";

const ColorCircularProgress = withStyles({
  root: {
    color: '#0F467A',
  },
})(CircularProgress);

const useStyles = makeStyles(theme=> ({
  backdrop: {
    zIndex: 111125,
    color: '#fff',
  },
}));

const QuizReportPdf = ({ quizData, quiz, date,attempt = 1, kidName ,...props }) => {
  const { i18n, t, company } = props;
  const isRTL = i18n.translator.language;
  const ar = "'Cairo', sans-serif";
  const en = "'Poppins', sans-serif";
  const fontFamily = isRTL === 'ar' ? ar : en;
  const companyName = company?.name || '';
  const { minScore, name: quizName } = quiz || {};
  const _minScore = minScore ? Number(minScore) : 0;
  const classes = useStyles();

  const [loading,setLoading] = React.useState(false);

  const generatePdf = async () => {
    setLoading(true);
    const doc = new jsPDF();
    const {
      questions,
      userInput,
      correctPoints,
      timeTaken = 0,
    } = quizData;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const pageHeight2 = doc.internal.pageSize.height;
    const lineHeight = 10; // Default line height
    const padding = 2;
    const footerMargin = 30; // Space for footer
    const tagBgColor = "#FAFAFA";
    const tagBorderColor = "#EDEDED";

    doc.addFileToVFS("Poppins-Regular.ttf", poppinsRegularBase64);
    doc.addFont("Poppins-Regular.ttf", "Poppins", "normal");

    doc.addFileToVFS("Poppins-Bold.ttf", poppinsBoldBase64);
    doc.addFont("Poppins-Bold.ttf", "Poppins", "bold");

    // Function to add header on each page
    const addHeader = () => {
      doc.setFont("Poppins", "bold");
      doc.setTextColor("#174F81");
      doc.setFontSize(14);
      doc.text("Quiz Report", pageWidth / 2, 10, { align: "center" });

      // Set the draw color to black for the border
      doc.setDrawColor(0, 0, 0); // Black color
      doc.line(10, 12, 200, 12); // Add bottom border for header
    };

    const loadImage = (url) =>
      new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "Anonymous";
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL("image/jpeg"));
        };
        img.onerror = (error) => reject(error);
        img.src = url;
      });

    // Add the header on the first page
    addHeader();

    if (timeTaken) TimeTaken(doc, timeTaken);

    let yOffset = 30;

    doc.setFont("Poppins", "bold");
    doc.setFontSize(16);
    doc.setTextColor('#174F81')
    doc.text("Details:", 10, yOffset);

    doc.setTextColor('#000000');

    yOffset += 5;
    
    doc.setFontSize(10);
    doc.setFillColor(tagBgColor); // light gray background
    doc.setDrawColor(tagBorderColor); // Gray border
    doc.roundedRect(10, yOffset, 35, 10, 1, 1, "FD"); // x, y, width, height, filled
    doc.setTextColor(0, 0, 0);
    let dateText = "Date: ";
    doc.setFont("Poppins", "normal");
    doc.text(dateText, 12, yOffset + 6);
    let dateTextWidth = doc.getTextWidth(dateText);
    doc.setFont("Poppins", "bold");
    doc.text(moment(new Date(date*1000)).format('DD/MM/YYYY'), 12 + dateTextWidth, yOffset + 6);

    // attempt
    doc.setFillColor(tagBgColor); // light gray background
    doc.setDrawColor(tagBorderColor); // Gray border
    doc.roundedRect(50, yOffset, 30, 10, 1, 1, "FD"); // x, y, width, height, filled
    doc.setTextColor(0, 0, 0);
    doc.setFont("Poppins", "normal");
    let attemptText = "Attempt: ";
    doc.text(attemptText, 52, yOffset + 6);
    let attemptTextWidth = doc.getTextWidth(attemptText);
    doc.setFont("Poppins", "bold");
    doc.text(attempt.toString(), 52 + attemptTextWidth, yOffset + 6);

    // score
    doc.setFillColor(tagBgColor); // light gray background
    doc.setDrawColor(tagBorderColor); // Gray border
    doc.roundedRect(85, yOffset, 28, 10, 1, 1, "FD"); // x, y, width, height, filled
    doc.setTextColor(0, 0, 0);
    doc.setFont("Poppins", "normal");
    let scoreText = "Score: ";
    doc.text(scoreText, 87, yOffset + 6);
    let scoreTextWidth = doc.getTextWidth(scoreText);
    doc.setFont("Poppins", "bold");
    doc.text(`${quizData.correctPoints}/${quizData.totalPoints}`, 87 + scoreTextWidth, yOffset + 6);

    // status
    doc.setFillColor(quizData.correctPoints >= _minScore ? "#EBF5DC" : "#FCEEED"); // light gray background
    doc.setDrawColor(quizData.correctPoints >= _minScore ? "#11B505" : "#9F483B"); // Gray border
    doc.roundedRect(118, yOffset, 28, 10, 1, 1, "FD"); // x, y, width, height, filled
    doc.setTextColor(quizData.correctPoints >= _minScore ? "#0C8D02" : "#C43B1F");
    doc.setFont("Poppins", "bold");
    let statusText = quizData.correctPoints >= _minScore ? "Passed" : "Failed";
    let statusTextWidth = doc.getTextWidth(statusText);
    let textX = 118 + 28 / 2 - statusTextWidth / 2;
    doc.text(statusText, textX, yOffset + 6);

    doc.setTextColor('#000000');
    yOffset += 20;

    // quiz name
    doc.setFont("Poppins", "normal");
    doc.setFontSize(12);
    doc.text("Quiz name:", 10, yOffset);
    doc.setFont("Poppins", "bold");
    doc.text(CapitalizeString(quizName), 55, yOffset);
    yOffset += lineHeight;

    // Full name details
    doc.setFont("Poppins", "normal");
    doc.setFontSize(12);
    doc.text("Full Name:", 10, yOffset);
    doc.setFont("Poppins", "bold");
    doc.text(CapitalizeString(kidName), 55, yOffset);
  
    yOffset += lineHeight * 3;

    doc.setTextColor('#000000');

    for (let index = 0; index < questions.length; index++) {
        const question = questions[index];
        // console.log(`Before Question ${index + 1} - yOffset:`, yOffset);
        doc.addPage();
        addHeader(); // Re-add header on new page
        yOffset = 30; // Reset yOffset after header
        doc.setTextColor('#000000');

        const userAnswer = userInput[index]; // Get the user's answer for this question
        const correctAnswer = question.correctAnswer; // The correct answer(s)
        const explanation = question.explanation;
        const explanationPic = question.explanationPic;
        const answerSelectionType = question.answerSelectionType;

        const userAnwserType =
            question.answerSelectionType === "single"
            ? userAnswer === undefined
                ? "unanswered"
                : userAnswer === Number(correctAnswer)
                ? "correct"
                : "incorrect"
            : question.answerSelectionType === "multiple"
            ? userAnswer === undefined
                ? "unanswered"
                : arraysMatch(userAnswer, correctAnswer)
                ? "correct"
                : "incorrect"
            : "";

        doc.setFontSize(11);
        doc.setTextColor('#000000');
        doc.setFont("Poppins", "bold");
        doc.text(`Question ${index + 1}/${questions.length}`, 10, yOffset);
        yOffset += 5;

            // mark tag
        doc.setFillColor("#F3F3F3"); // light gray background
        doc.setDrawColor("#F3F3F3");
        doc.roundedRect(10, yOffset, 28, 8, 1, 1, "FD"); // x, y, width, height, filled
        doc.setTextColor("#6D6969");
        doc.setFont("Poppins", "normal");
        doc.setFontSize(10);

        let markText = `Mark: ${question.point}`;
        let markTextWidth = doc.getTextWidth(markText);
        let markTextX = 10 + 28 / 2 - markTextWidth / 2;
        doc.text(markText, markTextX, yOffset + 5);

        yOffset += lineHeight + 6;


        // Add question title
        doc.setFont("Poppins", "bold");
        doc.setTextColor('#000000');
        doc.setFontSize(11);
        const splitText = doc.splitTextToSize(question.question, pageWidth - 2 * 10);
        doc.text(splitText, 10, yOffset);
        // doc.text(question.question, 10, yOffset);
        yOffset += splitText.length + lineHeight;

        // Question Image
        if (question.questionPic) {
            try {
            const base64Image = await loadImage(question.questionPic); // Load the image as base64
            const img = new Image();
            img.src = base64Image;

            // Wait for the image to load to get its dimensions
            await new Promise((resolve, reject) => {
                img.onload = () => resolve();
                img.onerror = (error) => reject(error);
            });

            // Calculate dimensions for the image
            const pageWidth = doc.internal.pageSize.getWidth();
            const margin = 10;
            const maxWidth = pageWidth - 2 * margin; // Maximum width available
            const imgAspectRatio = img.width / img.height;

            // Determine new dimensions for the image
            let newWidth, newHeight;

            if (img.width > maxWidth || img.height > 100) {
                if (imgAspectRatio > 1) {
                newWidth = maxWidth;
                newHeight = maxWidth / imgAspectRatio;
                } else {
                newHeight = 100; // Maximum height
                newWidth = 100 * imgAspectRatio;
                }
            } else {
                newWidth = img.width;
                newHeight = img.height;
            }

            // Center the image horizontally
            const x = (pageWidth - newWidth) / 2;
            const y = yOffset;

            // Add the image to the PDF
            doc.addImage(base64Image, "JPEG", x, y, newWidth, newHeight);

            // Update yOffset for the next element
            yOffset += newHeight + 10; // Adding a gap after the image
            } catch (error) {
            console.error("Error loading image:", error);
            }
        }

        // console.log(`After Question ${index + 1} - yOffset:`, yOffset);

        // Check if the user answered or not
        if (userAnswer === undefined) {
            // Apply "unanswered" style to all answers if no answer was selected
            question.answers.forEach((answer, answerIndex) => {
            let answerText = answer;
            let isCorrectAnswer =
                answerSelectionType === "single"
                ? `${answerIndex + 1}` === correctAnswer
                : correctAnswer?.includes(answerIndex + 1);

            if (isCorrectAnswer) {
                // Apply "unanswered" style to correct answers
                doc.setFillColor(255, 243, 224); // Light orange for unanswered
                doc.setDrawColor(255, 152, 0); // Orange border
            } else {
                // All other answers should be gray
                doc.setFillColor("#FAFAFA"); // Light gray
                doc.setDrawColor("#EAEAEA"); // Gray border
            }

            // Draw the colored box and text
            doc.roundedRect(10, yOffset, 190, 10, 1, 1, "FD");
            doc.setFont("Poppins", "normal");
            doc.setTextColor('#000000');
            doc.text(answerText, 15, yOffset + 7); // Adjust text position inside the box
            yOffset += lineHeight + 2; // Move down after each answer
            });
        } else {
            // User answered, so check each answer's correctness
            question.answers.forEach((answer, answerIndex) => {
            let answerText = answer;
            let isUserAnswer =
                answerSelectionType === "single"
                ? answerIndex + 1 === userAnswer
                : userAnswer?.includes(answerIndex + 1);

            let isCorrectAnswer =
                answerSelectionType === "single"
                ? `${answerIndex + 1}` === correctAnswer
                : correctAnswer?.includes(answerIndex + 1);

            if (isCorrectAnswer && isUserAnswer) {
                // Correct answer selected by the user
                doc.setFillColor(241, 248, 233); // Light green
                doc.setDrawColor(76, 175, 80); // Green border
            } 
            else if (!isCorrectAnswer && isUserAnswer) {
                // Incorrect answer selected by the user
                doc.setFillColor(255, 235, 238); // Light red
                doc.setDrawColor(244, 67, 54); // Red border
            } 
            else if (isCorrectAnswer) {
                // Correct answer not selected by the user
                doc.setFillColor(241, 248, 233); // Light green
                doc.setDrawColor(76, 175, 80); // Green border
            } 
            else if (isUserAnswer) {
              // Incorrect answer selected by the user
              doc.setFillColor(255, 235, 238); // Light red
              doc.setDrawColor(244, 67, 54); // Red border
            } 
            else {
                // Not selected and not correct (default style)
                doc.setFillColor("#FAFAFA"); // Light gray
                doc.setDrawColor("#EAEAEA"); // Gray border
            }

            // Draw the colored box and text
            doc.roundedRect(10, yOffset, 190, 10, 1, 1, "FD");
            doc.setFont("Poppins", "normal");
            doc.setTextColor('#000000');
            doc.text(answerText, 15, yOffset + 7); // Adjust text position inside the box
            yOffset += lineHeight + 2; // Move down after each answer
            });
        }

        const boxWidth = pageWidth - 2 * 10;

        yOffset += lineHeight; // Add space before explanation

        const splitExplanation = doc.splitTextToSize(
            explanation,
            boxWidth - 2 *10
        ); 
        const explanationHeight = splitExplanation.length * 12;
        const explanationLink = explanationPic ? 10 : 0;
        const totalBoxHeight = Math.max(20, explanationHeight > 50 ? (explanationHeight /2)+explanationLink : explanationHeight + padding * 2 + explanationLink);

        if (yOffset + totalBoxHeight + footerMargin > pageHeight2) {
            doc.addPage(); // Add a new page if content would overflow
            yOffset = 10; // Reset yOffset for the new page
        }

        doc.setFillColor(
            userAnwserType === "unanswered"
            ? "#FFFCDF"
            : userAnwserType === "incorrect"
            ? "#FCEEED"
            : "#EBF5DC"
        ); 
        doc.roundedRect(10, yOffset, boxWidth, explanation ? totalBoxHeight + explanationLink : 10, 2, 2, "F");
        doc.addImage(
            userAnwserType === "unanswered"
            ? unansweredIcon
            : userAnwserType === "incorrect"
            ? inCorrectIcon
            : correctIcon,
            "PNG",
            15,
            yOffset + 2, // Adjust placement based on yOffset
            5,
            5
        );

        doc.setFontSize(12);
        doc.setFont("Poppins", "bold");
        doc.setTextColor(
            userAnwserType === "unanswered"
            ? "#D58610"
            : userAnwserType === "incorrect"
            ? "#BF2F19"
            : "#0C8D02"
        );
        doc.text(
            userAnwserType === "unanswered"
            ? "Not Answered"
            : userAnwserType === "incorrect"
            ? "Your answer is incorrect."
            : "Your answer is correct.",
            23, // Offset from the icon
            yOffset + padding + 4
        );

        yOffset += 5;

        if (explanation || explanationPic) {
            doc.setFont("Poppins", "normal");
            doc.setFontSize(12);

            doc.text(splitExplanation, 15, yOffset + padding + 8);
            if(explanationPic) {
              yOffset += 10;
              doc.textWithLink(t && t('groupings.attachment'), 15, yOffset+padding+8, { url:explanationPic, decoration: 'underline' });
            };

            yOffset += totalBoxHeight + lineHeight;
        } else {
            yOffset += lineHeight; // Adjust if no explanation
        }
    }

    const pageCount = doc.internal.getNumberOfPages();

    // Loop over all pages and add the footer
    doc.setTextColor("#000000");
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(10);
      
        // Draw a line above the footer text
        const footerY = pageHeight - 20; // Position of the line and text (adjust as needed)
        doc.setDrawColor(0); // Set line color (black)
        doc.line(10, footerY, pageWidth - 10, footerY); // Draw a horizontal line

        // Add left-aligned text
        doc.setFont("Poppins", "normal");
        const leftText = `Generated at:  ${moment().format('DD/MM/YYYY h:mm:ss')}`;
        doc.text(leftText, 10, footerY + 10); // Add text below the line

        // Add right-aligned text (Page x of y)
        const rightText = `Page ${i} of ${pageCount}`;
        const rightTextWidth = doc.getTextWidth(rightText); // Get the width of the right-aligned text
        doc.setFont("Poppins", "normal");
        doc.text(rightText, pageWidth - rightTextWidth - 10, footerY + 10); // Align the right text to the right

        // Add center-aligned text
        const centerText = CapitalizeString(companyName);
        const centerTextWidth = doc.getTextWidth(centerText); // Get the width of the center text
        const centerX = (pageWidth / 2) - (centerTextWidth / 2); // Calculate the x position for centered text
        doc.setFont("Poppins", "bold");
        doc.text(centerText, centerX, footerY + 10);
    }

    // Save the PDF
    doc.save(`${quizName}_${companyName}_${moment(new Date(date*1000)).format('YYYY-MM-DD')}.pdf`);
    setLoading(false);
  };

  return (
    <div>
      <button type='button' style={{backgroundColor:'transparent',color:'#36ABEF',outline:'none',border:'none',cursor:'pointer',fontSize:'14px',fontFamily}} onClick={generatePdf}>{t('quiz.downloadReport')}</button>
      <Backdrop className={classes.backdrop} open={loading}>
        <ColorCircularProgress />
      </Backdrop>
    </div>
  );
};

const mapStateToProps = state => {
    return {
        user: state.user,
        company: state.company,
    };
};

export default connect(mapStateToProps,null)(withTranslation('common')(QuizReportPdf));

function CapitalizeString(title) {
    let companyName = title.split(" ");
    for (let i = 0; i < companyName.length; i++) {
        companyName[i] = companyName[i][0].toUpperCase() + companyName[i].substr(1);
    }

    companyName = companyName.join(" ");
    return companyName;
}
function TimeTaken(doc, time) {
  const formatTime = (time) => (time < 10 ? "0" : "");
  const displayTime = (time) => {
    const hours = Math.floor(time / 3600);
    const minutes = Math.floor((time % 3600) / 60);
    const seconds = time % 60;

    return `${formatTime(hours)}${hours}:${formatTime(
      minutes
    )}${minutes}:${formatTime(seconds)}${seconds}`;
  };

  doc.setFillColor("#EBF5FC"); // light blue background
  doc.setDrawColor("#EBF5FC"); // same color as border to make it invisible
  doc.roundedRect(160, 20, 35, 12, 6, 6, "FD");
  let timerText = time ? displayTime(time) : "";

  doc.setFont("Poppins", "bold");
  doc.setFontSize(10);
  let iconWidth = 8;
  let _textX = 160 + 16;
  let _textY = 20 + 12 / 2 + 1;
  let iconX = 160 + 5;
  let iconY = 20 + 2;
  doc.addImage(timeIcon, "PNG", iconX, iconY, iconWidth, iconWidth);
  doc.setTextColor("#42A5F5");
  doc.text(timerText, _textX, _textY);
}